"use client";

import React, { useEffect, useRef, useState } from "react";
import { Building2, ArrowRight, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function BusinessDirectoryHeader() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="business-directory-header" className="relative bg-background py-24 md:py-32 lg:py-40 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm">
            <Users className="mr-2 size-4" />
            Birkalla Community
          </Badge>

          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/wt_birkalla_logo_gold.png" alt="Birkalla Logo" className="h-20 w-auto object-contain mb-6 mx-auto" loading="lazy" />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground mb-6 tracking-tight leading-tight">
            Discover Our <span className="text-primary">Partner Network</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-10 font-default max-w-2xl mx-auto leading-relaxed">
            A curated directory of professional business partners committed to supporting and growing alongside the Birkalla community. Connect with trusted local services today.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto h-12 px-8 text-base" asChild>
              <Link to="#directory-listings">
                <Building2 className="mr-2 size-5" />
                Browse Directory
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 px-8 text-base" asChild>
              <Link to="/BirksBusinessNetworkPage">
                Join the Network
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}